import type {
  CheckIn,
  KeyResult,
  KeyResultProgressModeT,
  Objective,
  ObjectiveStatusT,
} from '@portfolio-pm/shared';
import { type FormEvent, useState } from 'react';
import {
  STATUS_OPTIONS,
  SectionHeading,
  fieldClass,
  formatDateInput,
  formatStatus,
  parseDateInput,
  primaryButtonClass,
  secondaryButtonClass,
  selectClass,
  shellCardClass,
} from './okr-ui.tsx';

/**
 * Editor forms for the OKR workspace: objective, key result, and
 * check-in capture. Extracted from OkrWorkspace.tsx.
 */

export function ObjectiveEditor({
  title,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Partial<Objective>;
  submitLabel: string;
  onSubmit(input: {
    title: string;
    description?: string;
    status: ObjectiveStatusT;
    startDate?: number;
    endDate?: number;
  }): void;
  onCancel(): void;
}): JSX.Element {
  const [form, setForm] = useState({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    status: (initial?.status ?? 'on_track') as ObjectiveStatusT,
    startDate: formatDateInput(initial?.startDate),
    endDate: formatDateInput(initial?.endDate),
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const startDate = parseDateInput(form.startDate);
    const endDate = parseDateInput(form.endDate);
    onSubmit({
      title: form.title,
      status: form.status,
      ...(form.description ? { description: form.description } : {}),
      ...(startDate !== undefined ? { startDate } : {}),
      ...(endDate !== undefined ? { endDate } : {}),
    });
  };

  return (
    <form onSubmit={submit} className={`${shellCardClass} space-y-4 p-4`}>
      <SectionHeading eyebrow="Editor" title={title} />
      <input
        value={form.title}
        onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
        placeholder="Objective title"
        className={fieldClass}
      />
      <textarea
        value={form.description}
        onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        placeholder="Description"
        rows={4}
        className={fieldClass}
      />
      <div className="grid gap-2 sm:grid-cols-3">
        <select
          value={form.status}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, status: event.target.value as ObjectiveStatusT }))
          }
          className={selectClass}
        >
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {formatStatus(status)}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={form.startDate}
          onChange={(event) => setForm((prev) => ({ ...prev, startDate: event.target.value }))}
          className={fieldClass}
        />
        <input
          type="date"
          value={form.endDate}
          onChange={(event) => setForm((prev) => ({ ...prev, endDate: event.target.value }))}
          className={fieldClass}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" className={primaryButtonClass}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function KeyResultEditor({
  title,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Partial<KeyResult>;
  submitLabel: string;
  onSubmit(input: {
    title: string;
    description?: string;
    progressMode: KeyResultProgressModeT;
    manualProgress?: number;
    startValue?: number;
    currentValue?: number;
    targetValue?: number;
    status?: ObjectiveStatusT;
    confidence?: number;
  }): void;
  onCancel(): void;
}): JSX.Element {
  const [form, setForm] = useState({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    progressMode: (initial?.progressMode ?? 'manual') as KeyResultProgressModeT,
    manualProgress: initial?.manualProgress?.toString() ?? '0',
    startValue: initial?.startValue?.toString() ?? '0',
    currentValue: initial?.currentValue?.toString() ?? '0',
    targetValue: initial?.targetValue?.toString() ?? '0',
    status: (initial?.status ?? '') as '' | ObjectiveStatusT,
    confidence: initial?.confidence?.toString() ?? '',
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const manualProgress =
      form.progressMode === 'manual' ? Number(form.manualProgress || 0) : undefined;
    const startValue = form.progressMode === 'numeric' ? Number(form.startValue || 0) : undefined;
    const currentValue =
      form.progressMode === 'numeric' ? Number(form.currentValue || 0) : undefined;
    const targetValue = form.progressMode === 'numeric' ? Number(form.targetValue || 0) : undefined;
    const confidence = form.confidence !== '' ? Number(form.confidence) : undefined;
    onSubmit({
      title: form.title,
      progressMode: form.progressMode,
      ...(form.description ? { description: form.description } : {}),
      ...(manualProgress !== undefined ? { manualProgress } : {}),
      ...(startValue !== undefined ? { startValue } : {}),
      ...(currentValue !== undefined ? { currentValue } : {}),
      ...(targetValue !== undefined ? { targetValue } : {}),
      ...(form.status ? { status: form.status } : {}),
      ...(confidence !== undefined ? { confidence } : {}),
    });
  };

  return (
    <form onSubmit={submit} className={`${shellCardClass} space-y-4 p-4`}>
      <SectionHeading eyebrow="Editor" title={title} />
      <input
        value={form.title}
        onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
        placeholder="Key result title"
        className={fieldClass}
      />
      <textarea
        value={form.description}
        onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        placeholder="Description"
        rows={4}
        className={fieldClass}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <select
          value={form.progressMode}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              progressMode: event.target.value as KeyResultProgressModeT,
            }))
          }
          className={selectClass}
        >
          <option value="manual">Manual</option>
          <option value="numeric">Numeric</option>
          <option value="task_rollup">Task rollup</option>
        </select>
        <select
          value={form.status}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, status: event.target.value as '' | ObjectiveStatusT }))
          }
          className={selectClass}
        >
          <option value="">No status</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {formatStatus(status)}
            </option>
          ))}
        </select>
      </div>

      {form.progressMode === 'manual' && (
        <input
          type="number"
          min={0}
          max={100}
          value={form.manualProgress}
          onChange={(event) => setForm((prev) => ({ ...prev, manualProgress: event.target.value }))}
          placeholder="Manual progress %"
          className={fieldClass}
        />
      )}

      {form.progressMode === 'numeric' && (
        <div className="grid gap-2 sm:grid-cols-3">
          <input
            type="number"
            value={form.startValue}
            onChange={(event) => setForm((prev) => ({ ...prev, startValue: event.target.value }))}
            placeholder="Start"
            className={fieldClass}
          />
          <input
            type="number"
            value={form.currentValue}
            onChange={(event) => setForm((prev) => ({ ...prev, currentValue: event.target.value }))}
            placeholder="Current"
            className={fieldClass}
          />
          <input
            type="number"
            value={form.targetValue}
            onChange={(event) => setForm((prev) => ({ ...prev, targetValue: event.target.value }))}
            placeholder="Target"
            className={fieldClass}
          />
        </div>
      )}

      <input
        type="number"
        min={0}
        max={100}
        value={form.confidence}
        onChange={(event) => setForm((prev) => ({ ...prev, confidence: event.target.value }))}
        placeholder="Confidence % (optional)"
        className={fieldClass}
      />

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" className={primaryButtonClass}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function CheckInCard({
  title,
  cadence,
  state,
  targets,
  onChange,
  onSubmit,
}: {
  title: string;
  cadence: 'daily' | 'weekly';
  state: {
    targetType: CheckIn['targetType'];
    targetId: string;
    note: string;
    blockers: string;
    nextSteps: string;
    statusUpdate: '' | ObjectiveStatusT;
    confidenceUpdate: string;
  };
  targets: {
    objectiveTargets: Array<{ id: string; label: string; targetType: 'objective' }>;
    keyResultTargets: Array<{ id: string; label: string; targetType: 'key_result' }>;
  };
  onChange(
    updater:
      | ((prev: {
          targetType: CheckIn['targetType'];
          targetId: string;
          note: string;
          blockers: string;
          nextSteps: string;
          statusUpdate: '' | ObjectiveStatusT;
          confidenceUpdate: string;
        }) => {
          targetType: CheckIn['targetType'];
          targetId: string;
          note: string;
          blockers: string;
          nextSteps: string;
          statusUpdate: '' | ObjectiveStatusT;
          confidenceUpdate: string;
        })
      | {
          targetType: CheckIn['targetType'];
          targetId: string;
          note: string;
          blockers: string;
          nextSteps: string;
          statusUpdate: '' | ObjectiveStatusT;
          confidenceUpdate: string;
        },
  ): void;
  onSubmit(): void;
}): JSX.Element {
  const pool =
    state.targetType === 'objective' ? targets.objectiveTargets : targets.keyResultTargets;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className={`${shellCardClass} space-y-3 p-4`}
    >
      <SectionHeading eyebrow="Check-in" title={title} />
      <div className="grid gap-2 sm:grid-cols-2">
        <select
          value={state.targetType}
          onChange={(event) =>
            onChange((prev) => ({
              ...prev,
              targetType: event.target.value as CheckIn['targetType'],
            }))
          }
          className={selectClass}
        >
          <option value="objective">Objective</option>
          <option value="key_result">Key Result</option>
        </select>
        <select
          value={state.targetId}
          onChange={(event) => onChange((prev) => ({ ...prev, targetId: event.target.value }))}
          className={selectClass}
        >
          {pool.length === 0 && <option value="">No targets</option>}
          {pool.map((target) => (
            <option key={target.id} value={target.id}>
              {target.label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={state.note}
        onChange={(event) => onChange((prev) => ({ ...prev, note: event.target.value }))}
        placeholder={cadence === 'daily' ? 'Micro update' : 'Weekly summary'}
        rows={3}
        className={fieldClass}
      />
      {cadence === 'weekly' && (
        <>
          <textarea
            value={state.blockers}
            onChange={(event) => onChange((prev) => ({ ...prev, blockers: event.target.value }))}
            placeholder="Blockers"
            rows={2}
            className={fieldClass}
          />
          <textarea
            value={state.nextSteps}
            onChange={(event) => onChange((prev) => ({ ...prev, nextSteps: event.target.value }))}
            placeholder="Next steps"
            rows={2}
            className={fieldClass}
          />
        </>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <select
          value={state.statusUpdate}
          onChange={(event) =>
            onChange((prev) => ({
              ...prev,
              statusUpdate: event.target.value as '' | ObjectiveStatusT,
            }))
          }
          className={selectClass}
        >
          <option value="">No status change</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {formatStatus(status)}
            </option>
          ))}
        </select>
        {state.targetType === 'key_result' ? (
          <input
            type="number"
            min={0}
            max={100}
            value={state.confidenceUpdate}
            onChange={(event) =>
              onChange((prev) => ({ ...prev, confidenceUpdate: event.target.value }))
            }
            placeholder="Confidence %"
            className={fieldClass}
          />
        ) : (
          <div className="rounded-md border border-dashed border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)]" />
        )}
      </div>
      <div className="flex justify-end">
        <button type="submit" className={primaryButtonClass}>
          Save {cadence}
        </button>
      </div>
    </form>
  );
}
